import React, { createContext, useContext, useState, useCallback, useRef, ReactNode } from 'react';
import { OptimizationService } from '../services/optimizationService';
import { OptimizationParameters, OptimizationResults, OptimizationResultsWithStats, ConvergenceData } from '../types';
import { saveResultsToHistory } from '../utils/historyStorage';
import { getOptimalParametersForTaskCount } from '../utils/parameterUtils';

export interface NotificationState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'warning' | 'info';
}

interface OptimizationContextType {
  isRunning: boolean;
  results: OptimizationResultsWithStats | null;
  convergenceData: ConvergenceData | null;
  progress: number;
  currentAlgorithm: string;
  currentIteration: number;
  totalIterations: number;
  currentRun: number;
  totalRuns: number;
  completedAlgorithms: Set<string>;
  notification: NotificationState | null;
  startOptimization: (params: OptimizationParameters, datasetText: string) => Promise<void>;
  startCompleteRun: (
    baseParams: OptimizationParameters,
    datasetText: string,
    workflowStart: number,
    workflowEnd: number,
    increment: number,
    onProgress?: (current: number, total: number, taskCount: number, params?: OptimizationParameters) => void
  ) => Promise<Array<{ taskCount: number; results: OptimizationResults; timestamp: string }>>;
  cancelOptimization: () => void;
  clearResults: () => void;
  showNotification: (message: string, severity?: 'success' | 'error' | 'warning' | 'info') => void;
  hideNotification: () => void;
}

const OptimizationContext = createContext<OptimizationContextType | undefined>(undefined);

export const useOptimization = () => {
  const context = useContext(OptimizationContext);
  if (!context) {
    throw new Error('useOptimization must be used within OptimizationProvider');
  }
  return context;
};

interface OptimizationProviderProps {
  children: ReactNode;
}

export const OptimizationProvider: React.FC<OptimizationProviderProps> = ({ children }) => {
  const [isRunning, setIsRunning] = useState(false);
  const [results, setResults] = useState<OptimizationResultsWithStats | null>(null);
  const [convergenceData, setConvergenceData] = useState<ConvergenceData | null>(null);
  const [progress, setProgress] = useState(0);
  const [currentAlgorithm, setCurrentAlgorithm] = useState('');
  const [currentIteration, setCurrentIteration] = useState(0);
  const [totalIterations, setTotalIterations] = useState(0);
  const [currentRun, setCurrentRun] = useState(0);
  const [totalRuns, setTotalRuns] = useState(1);
  const [completedAlgorithms, setCompletedAlgorithms] = useState<Set<string>>(new Set());
  const [notification, setNotification] = useState<NotificationState | null>(null);
  const [service] = useState(() => new OptimizationService());
  const cancelFlagRef = useRef(false);

  const showNotification = useCallback((message: string, severity: 'success' | 'error' | 'warning' | 'info' = 'info') => {
    setNotification({ open: true, message, severity });
  }, []);

  const hideNotification = useCallback(() => {
    setNotification(null);
  }, []);

  const startOptimization = useCallback(async (
    params: OptimizationParameters,
    datasetText: string
  ) => {
    // Reset cancel flag when starting new optimization
    cancelFlagRef.current = false;
    setIsRunning(true);
    setProgress(0);
    setResults(null);
    setConvergenceData(null);
    setCurrentAlgorithm('');
    setCurrentIteration(0);
    setTotalIterations(params.iterations);
    setCurrentRun(0);
    setTotalRuns(params.numberOfRuns || 1);
    setCompletedAlgorithms(new Set());

    try {
      // Load dataset
      service.loadDataset(datasetText, params.taskCount);

      // Run optimization (with statistics if multiple runs)
      const numberOfRuns = params.numberOfRuns || 1;
      const useStatistics = numberOfRuns > 1;

      const result = useStatistics
        ? await service.runOptimizationWithStatistics(
            params,
            (algorithm) => {
              // Algorithm start callback
              if (!cancelFlagRef.current) {
                setCurrentAlgorithm(algorithm);
                setCurrentIteration(0);
                const algName = algorithm.split(' (Run')[0]; // Remove run suffix for matching
                const algorithmIndex = ['GA', 'Cultural', 'SFLA', 'SFLAGA', 'SFLACA', 'PSO', 'GAPSO'].indexOf(algName);
                if (algorithmIndex >= 0) {
                  const baseProgress = (algorithmIndex / 7) * 100;
                  setProgress(baseProgress);
                }
              }
            },
            (algorithm, generation, fitness) => {
              // Generation update callback
              if (!cancelFlagRef.current) {
                setCurrentIteration(generation);
                setCurrentAlgorithm(algorithm);
                const algorithmIndex = ['GA', 'Cultural', 'SFLA', 'SFLAGA', 'PSO', 'GAPSO'].indexOf(algorithm);
                if (algorithmIndex >= 0) {
                  const baseProgress = (algorithmIndex / 7) * 100;
                  const algorithmProgress = ((generation / params.iterations) * 100) / 7;
                  const totalProgress = Math.min(100, baseProgress + algorithmProgress);
                  setProgress(totalProgress);
                }
              }
            },
            (runNumber, totalRuns, intermediateResults) => {
              if (!cancelFlagRef.current) {
                setCurrentRun(runNumber);
                // Update UI with intermediate results after each statistical run (PROGRESSIVE UPDATE)
                if (intermediateResults) {
                  const timestamp = new Date().toISOString();
                  setResults({
                    ...intermediateResults,
                    timestamp,
                  });
                }
              }
            },
            (algorithm, result, partialResults) => {
              // Update UI with partial results as each algorithm completes (same as complete run)
              if (!cancelFlagRef.current) {
                // Mark algorithm as completed
                setCompletedAlgorithms(prev => {
                  const newSet = new Set(prev);
                  newSet.add(algorithm);
                  return newSet;
                });
                
                // For statistics mode, only update results on the last run to show final statistics
                // But we can still show partial results during the last run
                // We'll track this by checking if we're on the last run via currentRun state
                // For now, update results progressively - final statistics will be set at the end
                setResults(prev => {
                  if (!prev) {
                    return {
                      ...partialResults,
                      configuration: {
                        taskCount: params.taskCount,
                        propertyCount: params.qosProperties,
                        initialPopulation: params.initialPopulation,
                        crossover: params.crossoverRate,
                        mutation: params.mutationRate,
                      },
                    } as OptimizationResultsWithStats;
                  }
                  return {
                    ...prev,
                    ...partialResults,
                  } as OptimizationResultsWithStats;
                });
              }
            },
            () => cancelFlagRef.current // shouldCancel callback
          )
        : await service.runOptimization(
            params,
            (algorithm) => {
              if (!cancelFlagRef.current) {
                // Check if cancelled before starting algorithm
                if (cancelFlagRef.current) return;
                setCurrentAlgorithm(algorithm);
                setCurrentIteration(0);
                const algorithmIndex = ['GA', 'Cultural', 'SFLA', 'SFLAGA', 'PSO', 'GAPSO'].indexOf(algorithm);
                const baseProgress = (algorithmIndex / 6) * 100;
                setProgress(baseProgress);
              }
            },
            (algorithm, generation, fitness) => {
              if (!cancelFlagRef.current) {
                setCurrentIteration(generation);
                setCurrentAlgorithm(algorithm);
                const algorithmIndex = ['GA', 'Cultural', 'SFLA', 'SFLAGA', 'PSO', 'GAPSO'].indexOf(algorithm);
                const baseProgress = (algorithmIndex / 6) * 100;
                const algorithmProgress = ((generation / params.iterations) * 100) / 6;
                const totalProgress = Math.min(100, baseProgress + algorithmProgress);
                setProgress(totalProgress);
              }
            },
            (algorithm, result, partialResults) => {
              // Update UI with partial results as each algorithm completes (same as complete run)
              if (!cancelFlagRef.current) {
                // Mark algorithm as completed
                setCompletedAlgorithms(prev => {
                  const newSet = new Set(prev);
                  newSet.add(algorithm);
                  return newSet;
                });
                
                // Update results progressively with partial results
                setResults(prev => {
                  if (!prev) {
                    // Initialize with partial results and configuration
                    return {
                      ...partialResults,
                      configuration: {
                        taskCount: params.taskCount,
                        propertyCount: params.qosProperties,
                        initialPopulation: params.initialPopulation,
                        crossover: params.crossoverRate,
                        mutation: params.mutationRate,
                      },
                    } as OptimizationResultsWithStats;
                  }
                  // Merge with existing results
                  return {
                    ...prev,
                    ...partialResults,
                  } as OptimizationResultsWithStats;
                });
              }
            },
            () => cancelFlagRef.current // shouldCancel callback
          );

      if (!cancelFlagRef.current) {
        const timestamp = new Date().toISOString();
        
        // Update UI with final results and convergence data (same as complete run)
        // Results may already be partially set from progressive updates, so merge with final results
        if (useStatistics) {
          // For statistics mode, result.results is OptimizationResultsWithStats
          setResults({
            ...(result.results as OptimizationResultsWithStats),
            timestamp,
          });
        } else {
          // For single run, result.results is OptimizationResults
          setResults({
            ...result.results,
            timestamp,
          } as OptimizationResultsWithStats);
        }
        setConvergenceData(result.convergenceData);
        setProgress(100);
        setCurrentAlgorithm('');
        setCurrentIteration(0);
        setTotalIterations(0);
        setCurrentRun(0);
        // Mark all algorithms as completed for final display
        setCompletedAlgorithms(new Set(['GA', 'Cultural', 'SFLA', 'SFLAGA', 'SFLACA', 'PSO', 'GAPSO']));
        
        // Save to history
        try {
          saveResultsToHistory(result.results);
        } catch (error) {
          console.warn('Failed to save results to history:', error);
        }
      }
    } catch (error) {
      // Don't show error notification if optimization was cancelled
      if (cancelFlagRef.current) {
        console.log('Optimization cancelled by user');
      } else {
        console.error('Optimization error:', error);
        showNotification(
          'Optimization failed: ' + (error instanceof Error ? error.message : String(error)),
          'error'
        );
      }
    } finally {
      if (!cancelFlagRef.current) {
        setIsRunning(false);
        setCurrentAlgorithm('');
        setCurrentRun(0);
      }
    }
  }, [service, showNotification]);

  const startCompleteRun = useCallback(async (
    baseParams: OptimizationParameters,
    datasetText: string,
    workflowStart: number,
    workflowEnd: number,
    increment: number,
    onProgress?: (current: number, total: number, taskCount: number, params?: OptimizationParameters) => void
  ): Promise<Array<{ taskCount: number; results: OptimizationResults; timestamp: string }>> => {
    // Reset cancel flag when starting new complete run
    cancelFlagRef.current = false;
    setIsRunning(true);
    setProgress(0);
    setResults(null);
    setConvergenceData(null);
    setCurrentAlgorithm('Complete Run');
    setCurrentIteration(0);
    setTotalIterations(0);
    setCompletedAlgorithms(new Set());
    const allResults: Array<{ taskCount: number; results: OptimizationResults; timestamp: string }> = [];
    
    // Calculate task counts
    const taskCounts: number[] = [];
    for (let taskCount = workflowStart; taskCount <= workflowEnd; taskCount += increment) {
      taskCounts.push(taskCount);
    }

    const totalRuns = taskCounts.length;
    setTotalRuns(totalRuns);
    setCurrentRun(0);

    try {
      for (let i = 0; i < taskCounts.length; i++) {
        if (cancelFlagRef.current) break;

        const taskCount = taskCounts[i];
        setCurrentRun(i + 1);
        setProgress((i / totalRuns) * 100);
        setCurrentAlgorithm(`Complete Run: ${taskCount} tasks (${i + 1}/${totalRuns})`);

        // Get optimal parameters for this task count
        const optimalParams = getOptimalParametersForTaskCount(taskCount);

        // Create parameters with optimal values for this task count
        const params: OptimizationParameters = {
          ...baseParams,
          taskCount,
          iterations: Math.floor(taskCount / 10) + 40, // iterations = (workflow tasks / 10) + 40
          initialPopulation: taskCount + 40, // initial population = workflow tasks + 40
          crossoverRate: optimalParams.crossoverRate,
          mutationRate: optimalParams.mutationRate,
          memeplexCount: optimalParams.memeplexCount,
          numberOfRuns: 10, // number of runs = 10
        };

        // Notify progress callback with current parameters for UI update
        if (onProgress) {
          onProgress(i + 1, totalRuns, taskCount, params);
        }

        // Load dataset with new task count
        service.loadDataset(datasetText, taskCount);

        // Set total iterations for this optimization run
        setTotalIterations(params.iterations);
        setCurrentIteration(0);

        // Track algorithms for this run - reset for each optimization run
        const algorithmsInOrder = ['GA', 'Cultural', 'SFLA', 'SFLAGA', 'SFLACA', 'PSO', 'GAPSO'];
        const runCompletedAlgorithms = new Set<string>();

        // Run optimization (with statistics if multiple runs)
        const numberOfRunsForStats = params.numberOfRuns || 1;
        const useStatistics = numberOfRunsForStats > 1;

        const result = useStatistics
          ? await service.runOptimizationWithStatistics(
              params,
              (algorithm) => {
                if (!cancelFlagRef.current) {
                  setCurrentAlgorithm(`Complete Run: ${taskCount} tasks - ${algorithm}`);
                  setCurrentIteration(0); // Reset iteration when algorithm starts
                }
              },
              (algorithm, generation, fitness) => {
                if (!cancelFlagRef.current) {
                  // Update current iteration for algorithm iteration progress bar
                  setCurrentIteration(generation);
                  
                  // Calculate overall progress: run progress + algorithm progress within run
                  const runProgress = (i / totalRuns) * 100;
                  const algorithmProgress = ((generation / params.iterations) * 100) / totalRuns;
                  setProgress(Math.min(100, runProgress + algorithmProgress));
                }
              },
              (runNumber, totalRunsForStats, intermediateResults) => {
                if (!cancelFlagRef.current && intermediateResults) {
                  // Update UI with intermediate results after each statistical run (PROGRESSIVE UPDATE)
                  // NOTE: Do NOT update currentRun here - it's already being used to track Complete Run task count progress
                  // The runNumber parameter is for statistical runs (1-10), not Complete Run task counts
                  const timestamp = new Date().toISOString();
                  setResults({
                    ...intermediateResults,
                    timestamp,
                  });
                  // Note: convergenceData is updated at the end from final result
                }
              },
              (algorithm, result, partialResults) => {
                // Update UI with partial results as each algorithm completes
                // Algorithm name includes run suffix like "GA (Run 1/10)", extract base name
                if (!cancelFlagRef.current) {
                  const algName = algorithm.split(' (Run')[0]; // Remove run suffix
                  // Algorithm completes after all runs are done (handled by runOptimizationWithStatistics)
                  // We'll mark all as complete after the full result is returned
                }
              }
            )
          : await service.runOptimization(
              params,
              (algorithm) => {
                if (!cancelFlagRef.current) {
                  setCurrentAlgorithm(`Complete Run: ${taskCount} tasks - ${algorithm}`);
                  setCurrentIteration(0); // Reset iteration when algorithm starts
                }
              },
              (algorithm, generation, fitness) => {
                if (!cancelFlagRef.current) {
                  // Update current iteration for algorithm iteration progress bar
                  setCurrentIteration(generation);
                  
                  // Calculate overall progress: run progress + algorithm progress within run
                  const runProgress = (i / totalRuns) * 100;
                  const algorithmProgress = ((generation / params.iterations) * 100) / totalRuns;
                  setProgress(Math.min(100, runProgress + algorithmProgress));
                  
                  // Mark algorithm as completed when it reaches final generation
                  if (generation >= params.iterations) {
                    runCompletedAlgorithms.add(algorithm);
                    setCompletedAlgorithms(new Set(runCompletedAlgorithms));
                  }
                }
              },
              undefined, // onAlgorithmComplete not used in single run mode
              () => cancelFlagRef.current // shouldCancel callback
            );
        
        // Mark all algorithms as completed after optimization finishes
        if (!cancelFlagRef.current) {
          algorithmsInOrder.forEach(alg => runCompletedAlgorithms.add(alg));
          setCompletedAlgorithms(new Set(runCompletedAlgorithms));
        }

        if (!cancelFlagRef.current) {
          const timestamp = new Date().toISOString();
          allResults.push({
            taskCount,
            results: result.results,
            timestamp,
          });

          // Update UI with current results and convergence data in real-time
          setResults({
            ...result.results,
            timestamp,
          });
          setConvergenceData(result.convergenceData);

          // Save to history
          try {
            saveResultsToHistory(result.results);
          } catch (error) {
            console.warn('Failed to save results to history:', error);
          }
        }
      }

      if (!cancelFlagRef.current) {
        setProgress(100);
        setCurrentAlgorithm('');
        setCurrentRun(0);
        setCurrentIteration(0);
        setTotalIterations(0);
        // Mark all algorithms as completed for final display
        setCompletedAlgorithms(new Set(['GA', 'Cultural', 'SFLA', 'SFLAGA', 'SFLACA', 'PSO', 'GAPSO']));
        // Results and convergenceData are already set after each iteration,
        // so the UI will show the last completed optimization with best results summary
        // Keep results set so best results summary is displayed
      }
    } catch (error) {
      // Don't show error notification if complete run was cancelled
      if (cancelFlagRef.current) {
        console.log('Complete run cancelled by user');
      } else {
        console.error('Complete run error:', error);
        showNotification(
          'Complete run failed: ' + (error instanceof Error ? error.message : String(error)),
          'error'
        );
      }
    } finally {
      if (!cancelFlagRef.current) {
        setIsRunning(false);
        setCurrentAlgorithm('');
        setCurrentRun(0);
      }
    }

    return allResults;
  }, [service, showNotification]);

  const cancelOptimization = useCallback(() => {
    // Set cancel flag to stop optimization loops
    cancelFlagRef.current = true;
    setIsRunning(false);
    setProgress(0);
    setCurrentAlgorithm('');
    setCurrentIteration(0);
    setTotalIterations(0);
    setCurrentRun(0);
    setTotalRuns(1);
    setCompletedAlgorithms(new Set());
    // Clear results to hide progress bar
    setResults(null);
    setConvergenceData(null);
  }, []);

  const clearResults = useCallback(() => {
    setResults(null);
    setConvergenceData(null);
    setProgress(0);
    setCompletedAlgorithms(new Set());
  }, []);

  return (
    <OptimizationContext.Provider
      value={{
        isRunning,
        results,
        convergenceData,
        progress,
        currentAlgorithm,
        currentIteration,
        totalIterations,
        currentRun,
        totalRuns,
        completedAlgorithms,
        notification,
        startOptimization,
        startCompleteRun,
        cancelOptimization,
        clearResults,
        showNotification,
        hideNotification,
      }}
    >
      {children}
    </OptimizationContext.Provider>
  );
};

